import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Layout, Plus, Pencil, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';

const PAGE_OPTIONS = ['homepage', 'buy', 'rent', 'projects', 'plots', 'commercial', 'agencies', 'agents', 'blog', 'news', 'property_detail', 'project_detail'];
const TIER_OPTIONS = ['Standard', 'Hot', 'Super Hot', 'Premium'];

interface Placement {
  id: string;
  code: string;
  name: string;
  description: string | null;
  location_label: string | null;
  width_px: number | null;
  height_px: number | null;
  available_spots: number;
  main_advantage: string | null;
  pages: string[];
  price_etb: number | null;
  tier_label: string | null;
  sort_order: number;
  is_active: boolean;
}

const blank: Partial<Placement> = {
  code: '', name: '', description: '', location_label: '',
  width_px: 300, height_px: 600, available_spots: 1,
  main_advantage: '', pages: [], price_etb: 0, tier_label: 'Standard',
  sort_order: 0, is_active: true,
};

export function BannerPlacementsManager() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Placement>>(blank);

  const { data, isLoading } = useQuery({
    queryKey: ['banner-placements-admin'],
    queryFn: async () => {
      const { data } = await supabase
        .from('banner_placements' as any)
        .select('*')
        .order('sort_order')
        .order('name');
      return (data as unknown as Placement[]) || [];
    },
  });

  const togglePage = (page: string) => {
    const set = new Set(editing.pages || []);
    set.has(page) ? set.delete(page) : set.add(page);
    setEditing({ ...editing, pages: Array.from(set) });
  };

  const save = async () => {
    if (!editing.code || !editing.name) {
      toast.error('Code and name are required');
      return;
    }
    const payload = {
      code: editing.code!.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
      name: editing.name,
      description: editing.description || null,
      location_label: editing.location_label || null,
      width_px: editing.width_px || null,
      height_px: editing.height_px || null,
      available_spots: editing.available_spots ?? 1,
      main_advantage: editing.main_advantage || null,
      pages: editing.pages || [],
      price_etb: editing.price_etb ?? null,
      tier_label: editing.tier_label || null,
      sort_order: editing.sort_order ?? 0,
      is_active: editing.is_active ?? true,
    };

    const op = editing.id
      ? supabase.from('banner_placements' as any).update(payload).eq('id', editing.id)
      : supabase.from('banner_placements' as any).insert(payload);
    const { error } = await op;
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(editing.id ? 'Placement updated' : 'Placement created');
    setOpen(false);
    setEditing(blank);
    qc.invalidateQueries({ queryKey: ['banner-placements-admin'] });
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this placement?')) return;
    const { error } = await supabase.from('banner_placements' as any).delete().eq('id', id);
    if (error) return toast.error(error.message);
    toast.success('Placement deleted');
    qc.invalidateQueries({ queryKey: ['banner-placements-admin'] });
  };

  const toggleActive = async (p: Placement) => {
    const { error } = await supabase
      .from('banner_placements' as any)
      .update({ is_active: !p.is_active })
      .eq('id', p.id);
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: ['banner-placements-admin'] });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Layout className="h-5 w-5 text-primary" /> Banner Placements
        </CardTitle>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(blank); }}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1" onClick={() => setEditing(blank)}>
              <Plus className="h-4 w-4" /> New Placement
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.id ? 'Edit placement' : 'New banner placement'}</DialogTitle>
              <DialogDescription>
                Defines an ad slot on the site (size, position, available spots, pricing).
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Code (slug) *</Label>
                  <Input
                    value={editing.code || ''}
                    onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                    placeholder="site_wide_right"
                  />
                </div>
                <div>
                  <Label>Display name *</Label>
                  <Input
                    value={editing.name || ''}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="Site Wide Right Banner"
                  />
                </div>
              </div>

              <div>
                <Label>Location label</Label>
                <Input
                  value={editing.location_label || ''}
                  onChange={(e) => setEditing({ ...editing, location_label: e.target.value })}
                  placeholder="Right vertical panel"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Width (px)</Label>
                  <Input
                    type="number" value={editing.width_px ?? ''}
                    onChange={(e) => setEditing({ ...editing, width_px: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Height (px)</Label>
                  <Input
                    type="number" value={editing.height_px ?? ''}
                    onChange={(e) => setEditing({ ...editing, height_px: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Available spots</Label>
                  <Input
                    type="number" min={1} value={editing.available_spots ?? 1}
                    onChange={(e) => setEditing({ ...editing, available_spots: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div>
                <Label>Main advantage</Label>
                <Input
                  value={editing.main_advantage || ''}
                  onChange={(e) => setEditing({ ...editing, main_advantage: e.target.value })}
                  placeholder="Excellent brand awareness and branding."
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={editing.description || ''}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div>
                <Label>Pages</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {PAGE_OPTIONS.map((p) => {
                    const active = (editing.pages || []).includes(p);
                    return (
                      <Badge
                        key={p}
                        variant={active ? 'default' : 'outline'}
                        className="cursor-pointer capitalize"
                        onClick={() => togglePage(p)}
                      >
                        {active && <Check className="h-3 w-3 mr-1" />}
                        {p.replace('_', ' ')}
                      </Badge>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Price (ETB / 30 days)</Label>
                  <Input
                    type="number" value={editing.price_etb ?? ''}
                    onChange={(e) => setEditing({ ...editing, price_etb: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Tier</Label>
                  <select
                    className="w-full h-10 rounded-md border bg-background px-3 text-sm"
                    value={editing.tier_label || ''}
                    onChange={(e) => setEditing({ ...editing, tier_label: e.target.value })}
                  >
                    {TIER_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Sort order</Label>
                  <Input
                    type="number" value={editing.sort_order ?? 0}
                    onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={editing.is_active ?? true}
                  onCheckedChange={(v) => setEditing({ ...editing, is_active: v })}
                />
                <Label>Active (visible to advertisers)</Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save}>{editing.id ? 'Save changes' : 'Create placement'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <Skeleton className="h-40" />
        ) : !data?.length ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No placements yet — create your first slot.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Spots</TableHead>
                <TableHead>Pages</TableHead>
                <TableHead>Price (ETB)</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{p.code}</div>
                  </TableCell>
                  <TableCell className="text-sm">{p.location_label || '—'}</TableCell>
                  <TableCell className="text-xs">
                    {p.width_px && p.height_px ? `${p.width_px}×${p.height_px}` : '—'}
                  </TableCell>
                  <TableCell className="text-center">{p.available_spots}</TableCell>
                  <TableCell className="text-xs">
                    <div className="flex flex-wrap gap-1 max-w-[200px]">
                      {p.pages.slice(0, 3).map((pg) => (
                        <Badge key={pg} variant="outline" className="text-[10px] capitalize">{pg}</Badge>
                      ))}
                      {p.pages.length > 3 && <Badge variant="outline" className="text-[10px]">+{p.pages.length - 3}</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{p.price_etb ? Number(p.price_etb).toLocaleString() : '—'}</TableCell>
                  <TableCell><Badge variant="outline">{p.tier_label || '—'}</Badge></TableCell>
                  <TableCell>
                    <Switch checked={p.is_active} onCheckedChange={() => toggleActive(p)} />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(p); setOpen(true); }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(p.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
